// Deteção automática de nova versão + limpeza de cache.
//
// __APP_VERSION__ é injetado no build (ver vite.config.ts) e é IGUAL ao valor
// escrito em /version.json no MESMO build. Quando o servidor passa a servir uma
// versão diferente da que está a correr no browser, limpamos toda a cache
// (Cache Storage + Service Workers) e recarregamos — sem o utilizador fazer nada.
// Resolve o ecrã branco da PWA instalada após um deploy (shell antiga em cache).

const CURRENT_VERSION = __APP_VERSION__;
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos
const GUARD_KEY = 'pm_app_version_reloaded_for';

// Overlay bloqueante visível durante a atualização (impede usar a versão antiga).
function showUpdatingOverlay(): void {
  try {
    if (document.getElementById('pm-updating-overlay')) return;
    const o = document.createElement('div');
    o.id = 'pm-updating-overlay';
    o.setAttribute('style', [
      'position:fixed', 'inset:0', 'z-index:2147483647',
      'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
      'background:#ffffff', 'gap:18px', 'font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
    ].join(';'));
    o.innerHTML =
      '<img src="/icons/pwa-192.png" alt="PadelMaster" width="84" height="84" style="margin-bottom:6px;border-radius:18px" />' +
      '<div style="width:34px;height:34px;border:3px solid #e0e0e0;border-top-color:#2e7d32;border-radius:50%;animation:pm-upd-spin .8s linear infinite"></div>' +
      '<p style="margin:0;color:#0a0a0a;font-size:16px;font-weight:600">A atualizar para a versão mais recente…</p>' +
      '<p style="margin:0;color:#6b7280;font-size:13px">Um momento, por favor.</p>' +
      '<style>@keyframes pm-upd-spin{to{transform:rotate(360deg)}}</style>';
    document.body.appendChild(o);
  } catch {
    /* se não der para mostrar, segue para o reload na mesma */
  }
}

async function clearAllCachesAndReload(targetVersion: string): Promise<void> {
  // Guarda anti-loop: só recarregamos uma vez por versão-alvo por sessão.
  try {
    if (sessionStorage.getItem(GUARD_KEY) === targetVersion) return;
    sessionStorage.setItem(GUARD_KEY, targetVersion);
  } catch {
    /* sessionStorage indisponível — segue na mesma */
  }

  showUpdatingOverlay();

  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore */
  }

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* ignore */
  }

  window.location.reload();
}

async function checkForUpdate(): Promise<void> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const data: { version?: string } = await res.json();
    if (data.version && data.version !== CURRENT_VERSION) {
      await clearAllCachesAndReload(data.version);
    }
  } catch {
    /* offline ou version.json indisponível — ignora silenciosamente */
  }
}

export function startVersionCheck(): void {
  // 1) logo ao abrir a app
  void checkForUpdate();
  // 2) periodicamente
  window.setInterval(() => void checkForUpdate(), CHECK_INTERVAL_MS);
  // 3) quando a aba/app volta a ficar visível
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  });
}
